import { EnvelopeClosedIcon, MobileIcon } from '@radix-ui/react-icons';

import { useThemeContext } from '../../contexts/ThemeContext';
import { resolveColor } from '../customTheme/theme/utils/resolveColor';
import { PoweredByQITech } from '../customTheme/PrivatePayrollAuction/components/PoweredByQITech';
import QISignTheme from '../../interfaces/Theme';
import * as S from './styles';

interface PostLoginMessageContact {
    type: 'email' | 'phone' | 'commercial_number' | string;
    label?: string;
    value?:
        | string
        | {
              international_dial_code?: string;
              area_code?: string;
              number?: string;
          };
}

interface PostLoginMessage {
    type: 'fraud_warning' | string;
    contacts?: PostLoginMessageContact[];
}

interface EnvelopeFolderPostLoginMessageProps {
    messages: PostLoginMessage[];
    resolvedTheme: QISignTheme;
    onDismiss: () => void;
}

const FRAUD_WARNING_BULLETS = [
    'Qualquer tipo de pagamento antecipado (PIX, transferência, boleto, etc.) para liberar crédito;',
    'Devolução de dinheiro para contas de terceiros – sejam pessoas ou empresas;',
    'Quitar dívidas de outros bancos como condição para liberar seu empréstimo;',
    'Reembolsos de benefícios sociais ou previdenciários;',
    'Boletos antecipados, a não ser quando você mesmo pedir para adiantar parcelas futuras.',
];

const formatPhoneNumber = (number: string): string => {
    const digits = number.replace(/\D/g, '');
    if (digits.length === 9) {
        return `${digits.slice(0, 5)}-${digits.slice(5)}`;
    }
    if (digits.length === 8) {
        return `${digits.slice(0, 4)}-${digits.slice(4)}`;
    }
    return number;
};

const formatContactValue = (contact: PostLoginMessageContact): string => {
    if (contact.type === 'phone' && typeof contact.value === 'object' && contact.value !== null) {
        const { area_code: areaCode, number } = contact.value;
        if (areaCode && number) {
            return `(${areaCode}) ${formatPhoneNumber(number)}`;
        }
        return number ? formatPhoneNumber(number) : '';
    }
    if (typeof contact.value === 'string') {
        return contact.value;
    }
    return '';
};

const ContactIconByType = ({ type }: { type: string }) => {
    if (type === 'email') {
        return <EnvelopeClosedIcon width={16} height={16} />;
    }
    return <MobileIcon width={16} height={16} />;
};

export const EnvelopeFolderPostLoginMessage = (props: EnvelopeFolderPostLoginMessageProps) => {
    const { messages, resolvedTheme, onDismiss } = props;

    const { clientThemeSettings } = useThemeContext();
    const theme: QISignTheme = clientThemeSettings ?? resolvedTheme;
    const primary = resolveColor('primary', theme);

    const fraudWarnings = (messages ?? []).filter((message) => message.type === 'fraud_warning');
    const otherMessages = (messages ?? []).filter((message) => message.type !== 'fraud_warning');

    return (
        <S.PageWrapper>
            <S.LogoSection>
                {theme.logo_url && <S.LogoImage src={theme.logo_url} alt="logo" />}
            </S.LogoSection>

            <S.ScrollContent>
                {fraudWarnings.map((message, index) => (
                    <S.WarningBlock key={`fraud-warning-${index}`}>
                        <S.Title $color={primary}>ATENÇÃO</S.Title>

                        <S.IntroText>NÃO cobramos e NUNCA vamos cobrar:</S.IntroText>

                        <S.BulletList>
                            {FRAUD_WARNING_BULLETS.map((bullet) => (
                                <S.BulletItem key={bullet}>
                                    <S.BulletMark>✕</S.BulletMark>
                                    <S.BulletText>{bullet}</S.BulletText>
                                </S.BulletItem>
                            ))}
                        </S.BulletList>

                        <S.HighlightText>Suspeitou? Denuncie!</S.HighlightText>
                        <S.InfoText>
                            Se recebeu algum tipo de solicitação, não pague nada e fale com a gente na hora:
                        </S.InfoText>

                        {Array.isArray(message.contacts) && message.contacts.length > 0 && (
                            <S.ContactList>
                                {message.contacts.map((contact, contactIndex) => (
                                    <S.ContactRow key={`contact-${index}-${contactIndex}`}>
                                        <S.ContactIcon>
                                            <ContactIconByType type={contact.type} />
                                        </S.ContactIcon>
                                        <S.ContactTextWrapper>
                                            <S.ContactLabel>{contact.label}</S.ContactLabel>
                                            <S.ContactValue>{formatContactValue(contact)}</S.ContactValue>
                                        </S.ContactTextWrapper>
                                    </S.ContactRow>
                                ))}
                            </S.ContactList>
                        )}
                    </S.WarningBlock>
                ))}

                {fraudWarnings.length === 0 &&
                    otherMessages.map((message, index) => (
                        <S.WarningBlock key={`post-login-message-${index}`}>
                            <S.Title $color={primary}>ATENÇÃO</S.Title>

                            {Array.isArray(message.contacts) && message.contacts.length > 0 && (
                                <S.ContactList>
                                    {message.contacts.map((contact, contactIndex) => (
                                        <S.ContactRow key={`contact-${index}-${contactIndex}`}>
                                            <S.ContactIcon>
                                                <ContactIconByType type={contact.type} />
                                            </S.ContactIcon>
                                            <S.ContactTextWrapper>
                                                <S.ContactLabel>{contact.label}</S.ContactLabel>
                                                <S.ContactValue>{formatContactValue(contact)}</S.ContactValue>
                                            </S.ContactTextWrapper>
                                        </S.ContactRow>
                                    ))}
                                </S.ContactList>
                            )}
                        </S.WarningBlock>
                    ))}
            </S.ScrollContent>

            <S.Footer>
                <S.DismissButton $color={primary} onClick={onDismiss}>
                    Estou ciente
                </S.DismissButton>
                <PoweredByQITech />
            </S.Footer>
        </S.PageWrapper>
    );
};
